<template>
  <div :class="{ disabled, [styleType]: true }">
    <slot name="label">
      <ui-label
        v-if="label"
        :name="name"
        :text="label"
        :help-tooltip="labelHelp"
      />
    </slot>
    <div
      class="ui-radio"
      :class="[{ 'horizontal-line': horizontalLine }, spaceBetweenLabelAndRadio]"
    >
      <slot />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, provide, toRef, useSlots } from 'vue';
import { uiRadioGroupContextKey } from '@/components/ui/ui-radio/context';

const props = withDefaults(
  defineProps<{
    modelValue?: unknown;
    disabled?: boolean;
    styleType?: string;
    label?: string;
    labelHelp?: string;
    color?: string;
    name?: string;
  }>(),
  {
    disabled: false,
    styleType: '',
    label: '',
  },
);

const emit = defineEmits<{
  'update:modelValue': [value: unknown];
}>();

const slots = useSlots();

provide(uiRadioGroupContextKey, {
  modelValue: toRef(props, 'modelValue'),
  disabled: toRef(props, 'disabled'),
  color: toRef(props, 'color'),
  styleType: toRef(props, 'styleType'),
  setModelValue: (value) => {
    emit('update:modelValue', value);
  },
});

const horizontalLine = computed(() => props.styleType === 'horizontal-line');

const spaceBetweenLabelAndRadio = computed(() => {
  if ((!slots.label && !props.label) || props.styleType === 'inline' || props.styleType === 'horizontal-line') {
    return;
  }
  return 'mt-4';
});
</script>

<style lang="scss" scoped>
.horizontal-line {
  .ui-radio {
    display: flex;
    align-items: flex-start;
    width: fit-content;

    :deep(.radiobutton) {
      flex-direction: column;
      align-items: center;

      .label {
        text-align: center;
      }

      .v-icon {
        margin-right: 0 !important;
      }

      .line {
        &:after,
        &:before {
          display: block;
          content: '';
          height: 1px;
          background: var(--color-gray20);
          position: absolute;
          width: 50%;
          top: 7px;
        }

        &:after {
          right: -5px;
        }

        &:before {
          left: -11px;
        }
      }

      &:last-child .line:after {
        display: none;
      }

      &:first-child .line:before {
        display: none;
      }
    }
  }
}

.inline {
  display: flex;
  width: fit-content;

  .ui-radio {
    display: flex;
    flex-flow: wrap;

    .ui-label {
      white-space: nowrap;
    }
  }
}

.inline-values {
  .ui-radio {
    display: flex;
    width: fit-content;
  }
}

.label {
  font-size: 13px;
  line-height: normal;
  color: var(--color-graphite);
}

.disabled {
  cursor: default;
}
</style>
